import { defineStore } from "pinia";
import ApiService from "../composables/ApiService";
import { useTranslation } from "../composables/Translation";
import { computed, ComputedRef, ref, Ref, watch } from "vue";
import { collect } from "collect.js";
import { useGA4 } from "../composables/GA4";
import { FilterFacet, FilterSection, Product, RangeValues } from "@daalder/types";

export interface FilterDataType extends FilterSection {
    selectedCheckboxes?: (string | boolean)[];
    configuredRange?: RangeValues;
    sort: {
        by: string;
        order: "asc" | "desc";
    };
}

export const useProductOverviewStore = defineStore("productOverview", () => {
    // Data
    const products: Ref<Product[]> = ref([]);
    const aggregations: Ref<any[]> = ref([]);
    const total_items = ref(0);
    const items_per_page = ref(30);

    const filters: Ref<FilterDataType[]> = ref([]);
    const filterQuestionsOptions: Ref<{
        selected: {
            option_name: string;
            product_overview_filters_data: any[];
        }[];
        skipped: { [key: string]: string };
    }> = ref({ selected: [], skipped: {} });
    const sortBy = ref("recommended");
    const page = ref(1);
    const loading = ref(true);
    const initialProductFetchingIsDone = ref(false);
    const shouldUpdateFiltersWithoutProductsRefetch = ref(false);
    const searchQuery: Ref<string | null> = ref(null);

    const propertiesToInclude = ref(["label"]);
    const additionalQueryParameters: Ref<{ name: string; value: string }[]> = ref([]);
    const preventVariationsWithFilters: Ref<boolean> = ref(false);
    const apiEndpoint: Ref<string> = ref("");
    const productListId: Ref<string> = ref(null);
    const productListName: Ref<string> = ref("");
    // const abortController: Ref<AbortController> = ref();

    let queryString: any = require("query-string");
    let apiService: ApiService = new ApiService();

    // Composables
    const { __t } = useTranslation();
    const { trackGAViewItemList, trackGASearch } = useGA4();

    // Computed properties
    const max_page = computed(() => {
        return Math.ceil(total_items.value / items_per_page.value);
    });

    // This computes the navigation (page) numbers to display
    const navigation_numbers = computed(() => {
        let firstPage: number;
        let length: number;

        if (max_page.value <= 7) {
            firstPage = 1;
            length = max_page.value;
        } else {
            if (page.value - 3 <= 1) {
                firstPage = 1;
            } else if (page.value + 3 >= max_page.value) {
                firstPage = max_page.value - 6;
            } else {
                firstPage = page.value - 3;
            }

            length = 7;
        }

        let navigation: (boolean | number)[] = Array(length)
            .fill(0)
            .map((e, i) => i + firstPage);

        //if page 1 isn-t in the set.
        if (navigation.indexOf(1) === -1) {
            navigation = [1, false].concat(navigation.slice(2));
        }

        //if last page isn-t in the set
        if (navigation.indexOf(max_page.value) === -1) {
            navigation = navigation.slice(0, 5).concat([false, max_page.value]);
        }

        return navigation as number[];
    });

    // This computes the filters that have valid checkbox values or a non-default range (so the customer adjusted it)
    const activeFilters = computed(() => {
        return filters.value.filter((filter) => {
            // Count the checkbox values that are not null/undefined/false
            let anyCheckboxesSelected = filter.selectedCheckboxes?.filter((checkboxValue) => checkboxValue)?.length > 0;

            // Get range filter checks
            let isRangeFilter = filter?.configuredRange !== undefined;
            let minRangeNotDefault: boolean =
                Math.floor(filter?.configuredRange?.min) !== Math.floor((filter.facets as RangeValues)?.min);
            let maxRangeNotDefault: boolean =
                Math.ceil(filter.configuredRange?.max) !== Math.ceil((filter.facets as RangeValues)?.max);

            // Return true if the filter has any values configured, false if not
            return anyCheckboxesSelected || (isRangeFilter && (minRangeNotDefault || maxRangeNotDefault));
        });
    });

    const parsedUrlFilters: ComputedRef<{ urlParamName: string; value: string }[]> = computed(() => {
        return activeFilters.value.map((filter) => {
            // If the filter is of type range
            if (filter.type === "range") {
                return {
                    urlParamName: `filter[${filter.key}]`,
                    value: `${filter.configuredRange.min}-${filter.configuredRange.max}`,
                };
            } else {
                // Else, the filter is assumed to be of type checkbox/select. Any other filter types are filtered
                // out by the 'activeFilters' computed property.
                let values: string = filter.selectedCheckboxes
                    .map((checkboxValue, index) => {
                        // If checkbox value is not null/undefined/false
                        if (checkboxValue) {
                            // Return the checkbox label (the facet name)
                            return (filter.facets as FilterFacet[])[index].name;
                        }
                    })
                    // Filter out any null/undefined values
                    .filter((facetName) => facetName)
                    .join("|");

                return {
                    urlParamName: `filter[${filter.key}]`,
                    value: values,
                };
            }
        });
    });

    // Watchers
    // This watcher rebuilds the url params and refetches the products when any of the filters change
    watch(
        filters,
        (newValue, oldValue) => {
            // Ignore changes in filters until init is done. This prevents event loops and incorrect api call cancellation
            // behaviour
            if (initialProductFetchingIsDone.value && !shouldUpdateFiltersWithoutProductsRefetch.value) {
                // Abort the currently running request. This allows the user to change a filter and then change it again
                // while the request is still going. The request will be cancelled and be rerun with the updated filter(s).
                // if(abortController.value) {
                //     abortController.value.abort();
                // }

                // Rebuild the url hash & params to match the current filters
                buildUrl();
                // Fetch the products using the current filters
                fetchProducts();
            }
        },
        { deep: true },
    );

    watch(shouldUpdateFiltersWithoutProductsRefetch, (newValue, oldValue) => {
        if (newValue) {
            updateRangeFiltersFromAggregations(aggregations.value);

            setTimeout(() => {
                shouldUpdateFiltersWithoutProductsRefetch.value = false;
            }, 1000);
        }
    });

    // When the url hash changes, parse the hash to filter params and apply those filters
    window.addEventListener("hashchange", () => {
        updateFiltersFromHash();
    });

    // Methods
    function init(
        propertiesToIncludeArg: string[] = [],
        additionalQueryParametersArg: { name: string; value: string }[] = [],
        preventVariationsWithFiltersArg: boolean,
        apiEndpointArg: string,
        categoryId: number,
        productListNameArg: string,
    ) {
        // Persist data received from ProductOverview.vue
        propertiesToInclude.value = propertiesToInclude.value.concat(propertiesToIncludeArg);
        additionalQueryParameters.value = additionalQueryParameters.value.concat(additionalQueryParametersArg);
        preventVariationsWithFilters.value = preventVariationsWithFiltersArg;
        apiEndpoint.value = apiEndpointArg;

        // Parse the url params
        searchQuery.value = queryString.parse(location.search)?.search;
        let parsedQueryString = queryString.parse(location.search);

        // Parse the sortBy value from the url
        if (parsedQueryString.sort) {
            sortBy.value = parsedQueryString.sort;
        }

        // Parse the current page value from the url
        if (parsedQueryString.pg) {
            page.value = parseInt(parsedQueryString.pg);
        }

        // Parse the items_per_page value from the url
        if (parsedQueryString.items_per_page) {
            items_per_page.value = parseInt(parsedQueryString.items_per_page);
        }

        // If this page is 'search'
        if (searchQuery.value) {
            // Set GA4 impression list name
            productListId.value = null;
            productListName.value = "search";

            trackGASearch(searchQuery.value.slice(1, searchQuery.value.length - 1));
        } else {
            // Set GA4 impression list name from the relevant prop
            if (categoryId) {
                productListId.value = categoryId.toString();
            }

            productListName.value = productListNameArg;
        }

        // Rebuild the url params to match the current filters. Don't update the hash, because that may contain filters
        // that we want to parse first.
        buildUrl(false);

        // Fetch the products using the current filters (none). Once the products are fetched, the valid filters are
        // parsed from the hash and the products are fetched again (using the parsed filters).
        fetchProducts();
    }

    async function doProductsAPICall() {
        loading.value = true;

        let params: {
            "filter[name,description,sku,search_aliasses]"?: string;
            "search[name]"?: string;
            "search[sku]"?: string;
            sort?: string;
            "filter[store_ids]"?: number;
            page?: number;
            "filter[is_published]"?: boolean;
            "filter[is_visible_in_overview]"?: boolean;
            per_page: number;
            properties: any[];
        } = getApiParams();

        // Get products using the filters
        return await apiService
            .get(apiEndpoint.value, {
                params: params,
            })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                throw error;
            });
    }

    async function fetchProducts(skipInitialSteps: boolean = false) {
        let response = await doProductsAPICall();

        if (response === undefined) {
            return;
        }

        // Update local values from response
        products.value = response.data.data.products;
        total_items.value = response.data.meta.total;
        items_per_page.value = response.data.meta.per_page;
        aggregations.value = response.data.data.aggregations?.data ?? [];
        // If current page index exceeds the max_page value
        if (page.value > max_page.value && max_page.value > 0) {
            // Re-build the url (this will fix the current page value)
            buildUrl();

            // Fetch the last existing page
            await fetchProducts();
        }

        // If this is the initial product fetch, and the value skipInitialSteps is false
        if (!initialProductFetchingIsDone.value && !skipInitialSteps) {
            // Format and save the ES aggregations from the response as filters. This will also parse the url hash (if
            // present) and update the filters to match it.
            setFiltersFromAggregations(response.data.data.aggregations.data);

            // Rebuild the url
            buildUrl();

            // If the url has any filters (from the hash or from the params), fetch the products again (this time with
            // the filters applied)
            if (getFiltersFromHash()?.length > 0 || getFiltersFromUrlParams()?.length > 0) {
                // 'skipInitialSteps: true' is passed to the recursive function call to prevent infinite loops.
                await fetchProducts(true);
            }

            // Setting this value to true 'enables' the filters watcher. That is why it is done at the last possible
            // moment. A delay was added because the watch on filters fired after the variable was set to True.
            // The products are already fetched again for the existing filters, so the products were fetched twice for the existing filters
            setTimeout(() => {
                initialProductFetchingIsDone.value = true;
            }, 1000);
        } else {
            shouldUpdateFiltersWithoutProductsRefetch.value = true;
        }

        // Preventing the tracking to be done twice if filters were set and the products have to be fetched again
        if (!skipInitialSteps) {
            trackGAViewItemList(products.value, productListName.value, productListId.value);
        }

        // Setting loading to false will make the product cards show.
        loading.value = false;
        // abortController.value = null;
    }

    /**
     * @todo: This return object type could be extracted into a type definition at the top of this file (it's re-used).
     */
    function getApiParams(): {
        "filter[name,description,sku,search_aliasses]"?: string;
        "search[name]"?: string;
        "search[sku]"?: string;
        sort?: string;
        "filter[store_ids]"?: number;
        page?: number;
        "filter[is_published]"?: boolean;
        "filter[is_visible_in_overview]"?: boolean;
        per_page: number;
        properties: any[];
    } {
        let params: {
            "filter[name,description,sku,search_aliasses]"?: string;
            "search[name]"?: string;
            "search[sku]"?: string;
            sort?: string;
            "filter[store_ids]"?: number;
            page?: number;
            "filter[is_published]"?: boolean;
            "filter[is_visible_in_overview]"?: boolean;
            per_page: number;
            properties: any[];
        } = {
            per_page: items_per_page.value,
            properties: propertiesToInclude.value,
        };

        // For each active filter
        activeFilters.value.forEach((filter) => {
            let key;

            // Some conditional logic to make sure the filter keys are set correctly.
            if (filter.key === "prices") {
                key = "filter[price]";
            } else if (filter.key === "shipping_time") {
                key = "filter[" + filter.key + ".name]";
            } else {
                key = `filter[properties.${filter.key}]`;
            }

            if (filter.type === "range") {
                // @ts-ignore
                params[key + "[gte]"] = filter.configuredRange.min;
                // @ts-ignore
                params[key + "[lte]"] = filter.configuredRange.max;
            } else {
                // @ts-ignore
                params[key] = filter.selectedCheckboxes
                    // Map selected checkboxes
                    .map((item, index) => {
                        // If checkbox value is true-ish (not false/null)
                        if (item) {
                            return (filter.facets as FilterFacet[])[index].name;
                        } else {
                            // Else, return null
                            return null;
                        }
                    })
                    // Filter null values
                    .filter((item) => item)
                    // Implode array
                    .join("|");
            }
        });

        // If search query is available, use it
        if (searchQuery.value) {
            // Prepend and append '*' to the search query, if it doesn't already have them
            let wildcardSearchQuery = searchQuery.value.match(/^\*\w+\*$/)
                ? searchQuery.value
                : `*${searchQuery.value}*`;
            params["search[name]"] = wildcardSearchQuery;
            params["search[sku]"] = wildcardSearchQuery;
        } else {
            params[`filter[is_published]`] = true;
        }

        // If sortBy value is available and isn't 'recommended', use it
        // Recommended sort order will be returned when not passing sort query parameter
        if (sortBy.value && sortBy.value !== "recommended") {
            params["sort"] = sortBy.value;
        }

        params["filter[store_ids]"] = window.store_id;
        params.page = page.value;

        // Logic for variations filtering
        if ((activeFilters.value.length === 0 && !searchQuery.value) || preventVariationsWithFilters.value) {
            params[`filter[is_visible_in_overview]`] = true;
        }

        // If any additional query params were passed to this Pinia store, use them
        additionalQueryParameters.value.forEach((param) => {
            // @ts-ignore
            params[param.name] = param.value;
        });

        return params;
    }

    /**
     * @todo Parameter should not be 'any'
     * @todo This could use some documentation in the form of comments
     */
    function setFiltersFromAggregations(newAggregations: any) {
        for (let section of newAggregations) {
            switch (section.type) {
                case "number":
                case "multiselect":
                case "select":
                case "checkbox":
                case "button":
                    section.type = "checkbox";
                    for (let facet of section.facets) {
                        facet.id = facet.name.replace(/[aeouijy]/g, "");
                    }
                    break;
                case "range":
                    section.configuredRange = {
                        min: Math.floor(section.facets.min),
                        max: Math.ceil(section.facets.max),
                    };
                    break;
                default:
                    break;
            }

            section.selectedCheckboxes = [];
            section.sort = {
                name: "name",
                order: "desc",
            };

            if (section.key === "prices") {
                section.name = __t("daalder-components.filters.price");
            } else if (section.key === "shipping_time") {
                section.name = __t("daalder-components.filters.shipping_time");
            }
        }

        // Save the formatted ES aggregations
        filters.value = newAggregations;

        // Remove invalid range filters. Changing a filter type from 'select' to 'range' in Daalder may break
        // functionality until the products are re-synced to ElasticSearch.
        filters.value = filters.value.filter((aggregation) => {
            if (aggregation.type === "range") {
                // Assert that the available range is greater than 0
                return (aggregation.configuredRange?.max ?? 0 - aggregation.configuredRange?.min ?? 0) > 0;
            }

            return true;
        });

        // Parse the filters contained in the base64-encoded hash
        updateFiltersFromHash();
        // Parse the filters from the url filter[] params. These overwrite the parsed hash filters, because they have
        // precedence.
        updateFiltersFromParams();
    }

    function updateFiltersFromHash() {
        // Get the filters from the hash
        let filtersFromHash = getFiltersFromHash();

        // If there's any filters parsed from the hash
        if (filtersFromHash !== null) {
            // Stop on JSON error
            let filtersCollection = collect(filters.value);

            // Iterate through the filters from the hash
            for (let filterKey in filtersFromHash) {
                // Attempt to find the local filter that matches the filter from the hash
                let filter: FilterDataType = filtersCollection.firstWhere("key", filterKey);

                if (filter === null) {
                    continue;
                }

                // If the filter is a range filter
                if (filtersFromHash[filterKey].hasOwnProperty("min")) {
                    // Set the filter to use the min/max values from the hash filter
                    filter.configuredRange = {
                        min: filtersFromHash[filterKey].min,
                        max: filtersFromHash[filterKey].max,
                    };
                    filter.filter_expanded = true;
                    continue;
                }

                // Else, the filter is (should be) a checkbox filter
                // Check if iterable
                // @ts-ignore
                if (filter.facets && typeof filter.facets[Symbol.iterator] === "function") {
                    filter.selectedCheckboxes = [];

                    // Set the filter to use the checkbox values from the hash filter
                    (filter.facets as FilterFacet[]).forEach((facet) => {
                        let matchingHashFilterValue = filtersFromHash[filterKey].find((hashFilterValue: any) => {
                            return matchesAggregationGroup(facet, hashFilterValue);
                        });

                        if (matchingHashFilterValue) {
                            filter.selectedCheckboxes.push(true);
                            filter.filter_expanded = true;
                        } else {
                            filter.selectedCheckboxes.push(false);
                        }
                    });
                }
            }
        }
    }

    function updateFiltersFromParams() {
        // Get all the current url parameter
        let urlFilterParams = getFiltersFromUrlParams();

        // For each set of valid filter data parsed from the URL parameters.
        urlFilterParams.forEach((filterFromUrl) => {
            if (filterFromUrl.type === "checkbox") {
                // Set the checkbox/select filter values parsed from the URL parameter
                setCheckboxFilter(filterFromUrl.filterKey, filterFromUrl.value as string[], true);
            } else if (filterFromUrl.type === "range") {
                // Set the range filter values parsed from the URL parameter
                setRangeFilter(filterFromUrl.filterKey, filterFromUrl.value as RangeValues);
            }
        });
    }

    function isValidFilter(filter: FilterDataType) {
        if (filter.type === "range") {
            return filter.configuredRange.max - filter.configuredRange.min !== 0;
        } else {
            return ((filter.facets as FilterFacet[]) ?? []).some((facet) => facet.id);
        }
    }

    function switchPerPage(perPage: number) {
        items_per_page.value = perPage;

        buildUrl();
        fetchProducts();
    }

    function switchPage(pageNumber: number, scroll = true) {
        page.value = pageNumber;
        buildUrl();
        fetchProducts();

        if (scroll) {
            window.scroll({
                top: window!.pageYOffset + document!.getElementById("pagecontent")!.getBoundingClientRect()!.top,
                behavior: "smooth",
            });
        }
    }

    function sortByOnChange(event: Event) {
        sortBy.value = (<HTMLSelectElement>event.target).value;
        buildUrl();
        fetchProducts();
    }

    function setRangeFilter(key: string, rangeValues: RangeValues) {
        let targetFilter: FilterDataType = getFilter(key);

        if (targetFilter && targetFilter.type === "range") {
            targetFilter.configuredRange = rangeValues;
        }
    }

    function setCheckboxFilter(key: string, checkboxes: string[], enable: boolean = true) {
        // Get the product filter using the key argument
        let targetFilter: FilterDataType = getFilter(key);

        // Prepare a list of Daalder filter types that are interpreted in the frontend as a checkbox/select filter.
        let checkboxTypes = ["number", "multiselect", "select", "checkbox", "button"];

        // If the product filter exists and matches the Daalder filter types list above
        if (targetFilter && checkboxTypes.includes(targetFilter.type)) {
            // For each checkbox value passed to this method
            checkboxes.forEach((checkbox) => {
                // Find the index of the checkbox value in the filter facets list
                let index = (targetFilter.facets as FilterFacet[]).findIndex((facet) => {
                    return facet.name == checkbox;
                });

                // If the index was found (meaning the filter value is valid)
                if (index !== -1) {
                    // Set the corresponding index on the checkbox boolean list for this filter to true or false.
                    // Whether the value is set to true or false is controlled by the 'enable' argument.
                    targetFilter.selectedCheckboxes[index] = enable;
                }
            });
        }
    }

    function getFiltersFromHash() {
        let hash = document.location.hash.substring(1);

        try {
            let filter = window.atob(hash);
            return JSON.parse(filter);
        } catch (e) {
            return null;
        }
    }

    function getFiltersFromUrlParams() {
        let urlParams = new URLSearchParams(window.location.search);
        let filteredUrlFilterParams: {
            filterKey: string;
            type: "checkbox" | "range";
            value: string[] | RangeValues;
        }[] = [];

        // For each current url parameter
        urlParams.forEach((value, key) => {
            // If the parameter is in the format filter[...], it represents a Product filter
            if (key.startsWith("filter[") && key.endsWith("]")) {
                // Parse the filter key from the parameter name
                let filterKey = key.replace("filter[", "").replace("]", "");

                // If a Product filter matching the parsed filter key exists
                if (getFilter(filterKey)) {
                    let parsedFilterValue: string[] | RangeValues;
                    let type: "checkbox" | "range";

                    // If the filter value is in the format {min}-{max} (note the '-'), it represents a range filter
                    // value.
                    if (/^\d+-\d+$/.test(value)) {
                        let min = parseInt(value.split("-")[0] ?? "0");
                        let max = parseInt(value.split("-")[1] ?? Number.MAX_SAFE_INTEGER.toString());

                        type = "range";
                        parsedFilterValue = {
                            min: min,
                            max: max,
                        };
                    } else if (value.includes("|")) {
                        // If the filter value is a '|'-separated list, it represents a checkbox/select filter value
                        // list.
                        type = "checkbox";
                        parsedFilterValue = value.split("|");
                    } else {
                        // If the filter value is a value containing neither '-' nor '|', it represents a single
                        // checkbox filter value.
                        type = "checkbox";
                        parsedFilterValue = [value];
                    }

                    filteredUrlFilterParams.push({
                        filterKey: filterKey,
                        type: type,
                        value: parsedFilterValue,
                    });
                }
            }
        });

        return filteredUrlFilterParams;
    }

    /**
     * @todo This could use some documentation in the form of comments
     */
    function matchesAggregationGroup(facet: any, value: any) {
        return (
            facet.id === value ||
            facet.name === value ||
            facet.id.replace(/ /g, "") === value ||
            facet.name.replace(/ /g, "") === value
        );
    }

    function buildUrl(overwriteUrlFilterParams: boolean = true, onlyReturnUrl: boolean = false): void | string {
        let parsedUrlFiltersCopy: { urlParamName: string; value: string }[] = [];

        // If the url params should be rebuild from the filter data in this Pinia store
        if (overwriteUrlFilterParams) {
            // Map the active filters
            parsedUrlFiltersCopy = parsedUrlFilters.value;
        }

        // If current page index exceeds the new max_page value
        if (page.value > max_page.value && max_page.value > 0) {
            // Set the current page index to max_page to prevent loading an out-of-bounds page index
            page.value = max_page.value;
        } else if (page.value <= 0) {
            // Else if the current page index is less than or equal to zero, set it to 1 (first page)
            page.value = 1;
        }

        // Get search query (url params)
        let urlSearchParams: URLSearchParams;

        // If overwriteUrlFilterParams is true, discard the current url parameters. They are then rebuild later on in
        // this method.
        if (overwriteUrlFilterParams) {
            urlSearchParams = new URLSearchParams();
        } else {
            urlSearchParams = new URLSearchParams(location.search);
        }

        // Update the standard parameters
        urlSearchParams.set("sort", sortBy.value);
        urlSearchParams.set("pg", page.value.toString());
        urlSearchParams.set("items_per_page", items_per_page.value.toString());

        if (searchQuery.value) {
            urlSearchParams.set("search", searchQuery.value);
        }

        // If overwriteUrlFilterParams is true, set/update the url filter params
        if (overwriteUrlFilterParams) {
            parsedUrlFiltersCopy.forEach((parsedUrlFilter) => {
                urlSearchParams.set(parsedUrlFilter.urlParamName, parsedUrlFilter.value);
            });
        }

        // Prepare updated url
        let newLocation: string = window.location.pathname + "?" + decodeURIComponent(urlSearchParams.toString());

        // If the url is not overwritten, keep the hash. It will be replaced by url filter params later on.
        if (!overwriteUrlFilterParams) {
            newLocation += window.location.hash;
        }

        // If this is essentially a dry run
        if (onlyReturnUrl) {
            return newLocation;
        } else {
            // Overwrite the current history state using the new url
            window.history.replaceState({}, "", newLocation);
        }
    }

    function clearFilter(filterKey: string, filterValue: string = null) {
        let filter = collect(filters.value).where("key", filterKey).first();

        if (filter.type === "range") {
            filter.configuredRange.min = Math.floor((filter.facets as RangeValues).min);
            filter.configuredRange.max = Math.ceil((filter.facets as RangeValues).max);
        } else {
            // If a specific value to clear was set
            if (filterValue) {
                // Find the index of the value
                let index = (filter.facets as FilterFacet[]).findIndex((facet) => {
                    return facet.name === filterValue;
                });

                // Clear the checkbox for that value
                filter.selectedCheckboxes[index] = false;
            } else {
                // Else, clear the filter entirely
                filter.selectedCheckboxes = [];
            }
        }
    }

    function clearAllFilters() {
        filters.value.forEach((filter) => {
            clearFilter(filter.key);
        });

        filterQuestionsOptions.value.skipped = {};
    }

    function getFilter(key: string): FilterDataType {
        return filters.value.find((filter) => {
            return filter.key === key;
        });
    }

    function getHasProductsIfFilterWouldBeAdded(
        key: string,
        rangeValue: RangeValues = null,
        checkboxes: string[] = null,
    ) {
        if (!!rangeValue && !!checkboxes) {
            throw new Error(
                "This method supports a RangeValues object _or_ a checkboxes array, not both at the same time!",
            );
        }

        if (!aggregations.value?.length) {
            return false;
        }

        let aggregation = aggregations.value.find((aggregation) => {
            return aggregation.key === key;
        });

        if (aggregation && !!checkboxes && checkboxes.length > 0) {
            // Determine the facets that are valid and result in more than 0 product results, filtered by the checkbox names.
            let facetsForCheckboxes = checkboxes.filter((checkboxName: string) => {
                let filterFacet = aggregation.facets.find((facet: any) => {
                    return facet.name === checkboxName;
                });

                return !!filterFacet && filterFacet?.amount > 0;
            });

            // If all checkboxes filter values result in more than 0 products, this entire filter results in more than 0
            // products.
            return facetsForCheckboxes.length === checkboxes.length;
        } else if (!!rangeValue) {
            // Assert range overlap between passed filter and aggregation
            if (rangeValue.min < aggregation.min) {
                return rangeValue.max > aggregation.min;
            }

            // Assert range overlap between passed filter and aggregation
            if (rangeValue.min > aggregation.min) {
                return rangeValue.min < aggregation.max;
            }

            // Fallback on false
            return false;
        }

        return false;
    }

    /**
     * @todo This could use some documentation in the form of comments
     */
    function updateRangeFiltersFromAggregations(newAggregations: any) {
        for (let section of filters.value) {
            if (
                section.type === "range" &&
                !activeFilters.value.find((activeFilter) => activeFilter.key === section.key)
            ) {
                let updatedAggregation = newAggregations.find(
                    (aggregation: FilterDataType) => aggregation.key === section.key,
                );

                if (updatedAggregation) {
                    section.configuredRange = section.facets = {
                        min: Math.floor(updatedAggregation.facets.min),
                        max: Math.ceil(updatedAggregation.facets.max),
                    };
                }
            }
        }
    }

    return {
        products,
        total_items,
        items_per_page,
        filters,
        sortBy,
        page,
        loading,
        productListName,
        productListId,
        max_page,
        navigation_numbers,
        activeFilters,
        init,
        isValidFilter,
        switchPerPage,
        switchPage,
        sortByOnChange,
        setRangeFilter,
        setCheckboxFilter,
        clearFilter,
        clearAllFilters,
        getFilter,
        getHasProductsIfFilterWouldBeAdded,
        filterQuestionsOptions,
    };
});
